import { z } from "zod";

import dayjs, { Dayjs } from "../../../config/dayjs";
import { leaveType, leaveStatus } from "../../../constant/enum";
import { IUserResponse } from "../../user/type/user";

// Custom Zod transformer for Dayjs to Date conversion
const dayjsToDate = z
  .union([
    z.date(),
    z.custom<Dayjs>((val) => dayjs.isDayjs(val), {
      message: "Expected a valid date",
    }),
  ])
  .transform((val) => {
    if (dayjs.isDayjs(val)) {
      return val.toDate();
    }
    return val;
  });

// Base leave fields that are common to both request and response
const BaseLeaveSchema = z.object({
  leaveType: z.enum(leaveType as [string, ...string[]]),
  startDate: dayjsToDate,
  endDate: dayjsToDate,
  reason: z.string(),
  status: z
    .enum(leaveStatus as [string, ...string[]])
    .default("pending")
    .optional(),
});

// Schema for creating/updating leaves
export const LeaveRequestSchema = BaseLeaveSchema.extend({
  totalDays: z.number().positive().optional(),
  fileUrl: z.string().url().optional(),
});

const UpdateLeaveStatusRequestSchema = z.object({
  status: z.enum(leaveStatus as [string, ...string[]]).optional(),
});

// Schema for leave responses from API
export const LeaveResponseSchema = BaseLeaveSchema.extend({
  id: z.string(),
  user: z.custom<IUserResponse>(),
  totalDays: z.number().positive().optional(),
  fileUrl: z.string().url().optional(),
  approvedByUser: z.custom<IUserResponse>().optional(),
  approvedAt: z.date().optional(),
  rejectedByUser: z.custom<IUserResponse>().optional(),
  rejectedAt: z.date().optional(),
  rejectedReason: z.string().optional(),
  archive: z.date().optional(),
});

export type ILeaveRequest = z.infer<typeof LeaveRequestSchema>;
export type ILeaveResponse = z.infer<typeof LeaveResponseSchema>;
export type IUpdateLeaveStatusRequest = z.infer<
  typeof UpdateLeaveStatusRequestSchema
>;

export interface IApiResponseGetLeaves {
  results: ILeaveResponse[];
  page: string;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export interface IApiResponseGetUserLeaveBalanceByType {
  balance: number;
  used: number;
  total: number;
}
